/**
 * Access to the shared channel rule table, JavaScript side.
 *
 * The JSON is imported from src/Classification/ -- the same file PHP reads at runtime --
 * so both runtimes classify against one list of search engines, one list of click IDs
 * and one list of social hosts. esbuild inlines it at build time, so there is no fetch
 * and no second copy to keep in step.
 *
 * Runtime overrides arrive from PHP via window.ccAttrConfig.rules, which is how the
 * cc_attr_click_ids / cc_attr_channel_rules filters reach the browser. That is what lets
 * marketing add a click ID without a JS deploy.
 */

import ruleData from '../../../src/Classification/channel-rules.json' with { type: 'json' };

/**
 * Lowercases a hostname and strips the parts that are never significant.
 *
 * @param {string} host Raw hostname, possibly with scheme, port or trailing dot.
 * @return {string} Normalised hostname, or an empty string.
 */
export function normaliseHost( host ) {
	let value = String( host || '' ).trim().toLowerCase();

	if ( value.includes( '//' ) ) {
		try {
			value = new URL( value ).hostname.toLowerCase();
		} catch {
			return '';
		}
	}

	const colon = value.indexOf( ':' );

	if ( colon !== -1 ) {
		value = value.slice( 0, colon );
	}

	return value.replace( /\.+$/, '' );
}

/**
 * Builds the rule accessor used by the classifier.
 *
 * @param {object}   [options]               Options.
 * @param {string[]} [options.internalHosts] Hostnames this site owns.
 * @param {object}   [options.overrides]     Partial rule table merged over the built-in one.
 * @return {object} Rule accessor.
 */
export function createRules( options = {} ) {
	const overrides = options.overrides || {};
	const data = {
		...ruleData,
		...overrides,
		campaignParams: { ...ruleData.campaignParams, ...( overrides.campaignParams || {} ) },
		mediums: { ...ruleData.mediums, ...( overrides.mediums || {} ) },
		sourceTokens: { ...ruleData.sourceTokens, ...( overrides.sourceTokens || {} ) },
		referrerHosts: { ...ruleData.referrerHosts, ...( overrides.referrerHosts || {} ) },
		brandHostTokens: { ...ruleData.brandHostTokens, ...( overrides.brandHostTokens || {} ) },
		appReferrers: { ...ruleData.appReferrers, ...( overrides.appReferrers || {} ) },
		emailHostPrefixes: overrides.emailHostPrefixes || ruleData.emailHostPrefixes || [],
		excludedReferrers: [ ...( ruleData.excludedReferrers || [] ), ...( overrides.excludedReferrers || [] ) ],
	};

	// Click IDs merge by parameter name so an override can redefine gclid rather than
	// only append to the list.
	const clickIds = new Map();

	for ( const entry of ruleData.clickIds ) {
		clickIds.set( String( entry.param ).toLowerCase(), entry );
	}

	for ( const entry of overrides.clickIds || [] ) {
		if ( entry && typeof entry.param === 'string' ) {
			clickIds.set( entry.param.toLowerCase(), entry );
		}
	}

	const internalHosts = ( options.internalHosts || [] )
		.map( ( host ) => normaliseHost( host ) )
		.filter( Boolean );

	return {
		campaignParams( field ) {
			const list = data.campaignParams[ field ];
			return Array.isArray( list ) ? list : [];
		},

		clickIds() {
			return clickIds;
		},

		mediums( className ) {
			const list = data.mediums[ className ];
			return Array.isArray( list ) ? list : [];
		},

		sourceTokens() {
			return data.sourceTokens || {};
		},

		referrerHosts() {
			return data.referrerHosts || {};
		},

		brandHostTokens() {
			return data.brandHostTokens || {};
		},

		/**
		 * Android package name => platform, for android-app:// referrers only.
		 *
		 * @return {object} Map of package name to platform.
		 */
		appReferrers() {
			return data.appReferrers || {};
		},

		/**
		 * First host labels that mark a referrer as webmail.
		 *
		 * @return {string[]} Labels.
		 */
		emailHostPrefixes() {
			return ( data.emailHostPrefixes || [] ).map( ( label ) => String( label ).toLowerCase() );
		},

		/**
		 * Whether a referring host is a sign-in or payment provider: never a touch.
		 *
		 * Exact host or a subdomain of a listed one, as ChannelRules::isExcludedReferrer().
		 *
		 * @param {string} host Hostname to test.
		 * @return {boolean} True when excluded.
		 */
		isExcludedReferrer( host ) {
			const value = normaliseHost( host );

			if ( value === '' ) {
				return false;
			}

			return data.excludedReferrers.some( ( entry ) => {
				const domain = String( entry ).toLowerCase();
				return value === domain || value.endsWith( '.' + domain );
			} );
		},

		internalHosts() {
			return internalHosts.slice();
		},

		/**
		 * Whether a hostname belongs to this site.
		 *
		 * Exact membership of an explicit list. Deliberately NOT a registrable-domain
		 * comparison: hostname.split('.').slice(-2) treats example.co.uk as the TLD
		 * `co.uk` and then matches every unrelated site under it, which silently breaks
		 * both the direct-view rule and internal UTM-leak detection.
		 *
		 * @param {string} host Hostname to test.
		 * @return {boolean} True when the host is one of ours.
		 */
		isInternalHost( host ) {
			const value = normaliseHost( host );
			return value !== '' && internalHosts.includes( value );
		},
	};
}
